import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FleetSection, RollupTiles } from '../../../../shared/sso-workspace/fleet-page'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { formatAgo, formatCount, formatDateTime } from '../../../../shared/sso-workspace/format'
import { serverMessage } from '../../../../shared/error/server-message'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { LiveIndicator } from '../../../../shared/table-builder/component/live-indicator'
import { useOrganizationName } from '../../../../shared/org-search/use-organization-names'
import { getHealth, getMetrics } from '../../../../api-new/state/notify'
import { channelLabel, circuitLabel, circuitTone, durationText, laneLabel, providerLabel, providerModeLabel, providerModeTone } from '../../../../shared/notify/words'
import { NOTIFY_BASE } from './paths'

// The dashboard's Live refresh: a background read (edxr-bg=1) of health
// and metrics every 30 seconds while the tab is visible, at once when it
// shows again; never a 5-second poll.
export const LIVE_SECONDS = 30

const WINDOWS = [{ key: '1h', label: 'Last hour' }, { key: '24h', label: 'Last 24 hours' }, { key: '7d', label: 'Last 7 days' }]
const STATUS = { ok: { label: 'Healthy', tone: 'ok' }, degraded: { label: 'Degraded', tone: 'warn' }, failing: { label: 'Failing', tone: 'bad' } }

const rateTone = p => (p > 10 ? 'bad' : p > 2 ? 'warn' : 'ok')
const percent = p => (p === null || p === undefined ? '—' : `${Math.round(Number(p) * 10) / 10}%`)

const LimitPill = ({ breached }) => (breached ? <Pill tone="bad">Over the limit</Pill> : <Pill tone="ok">Within the limit</Pill>)

const OrgName = ({ id }) => useOrganizationName(id) || `Organization ${id}`

// The staff Notifications overview (spec sections 13, 13.1): volumes and
// failure rates by channel, lane depth and lag, oldest pending, the xmin
// and long-transaction guardrails (OD-2), provider health and circuit
// state, and the budgets in force. C-4 GET /notify/health and /metrics.
export const OverviewTab = () => {
  const [windowKey, setWindowKey] = useState('24h')
  const [health, setHealth] = useState(undefined)
  const [metrics, setMetrics] = useState(undefined)
  const [error, failure, setError] = useErrorLine()
  const [live, setLive] = useState({ health: 'live', lastUpdatedAt: null })
  const mounted = useRef(true)
  const windowRef = useRef(windowKey)
  windowRef.current = windowKey
  const loaded = useRef(false)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const read = useCallback(async ({ background }) => {
    try {
      const [h, m] = await Promise.all([getHealth({ background }), getMetrics({ window: windowRef.current }, { background })])
      if (!mounted.current) return
      setHealth(h || {})
      setMetrics(m || {})
      setError(null)
      loaded.current = true
      setLive({ health: 'live', lastUpdatedAt: new Date() })
    } catch (err) {
      if (!mounted.current) return
      if (loaded.current) setLive(l => ({ ...l, health: 'reconnecting' }))
      else setError(serverMessage(err, "Notification health couldn't be loaded."), err)
    }
  }, [setError])

  // The first read is the person's (they opened the page or chose a window).
  useEffect(() => {
    read({ background: false })
  }, [windowKey, read])

  useEffect(() => {
    let timer = null
    const start = () => { if (!timer) timer = setInterval(() => read({ background: true }), LIVE_SECONDS * 1000) }
    const stop = () => { if (timer) clearInterval(timer); timer = null }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') { read({ background: true }); start() } else stop()
    }
    document.addEventListener('visibilitychange', onVisibility)
    if (document.visibilityState === 'visible') start()
    return () => { stop(); document.removeEventListener('visibilitychange', onVisibility) }
  }, [read])

  const windowSelect =
    <div className="d-flex align-items-center gap-2">
      <select className="form-select form-select-sm" aria-label="Window" value={windowKey} onChange={e => setWindowKey(e.target.value)}>
        {WINDOWS.map(w => <option key={w.key} value={w.key}>{w.label}</option>)}
      </select>
      <LiveIndicator health={live.health} lastUpdatedAt={live.lastUpdatedAt} />
    </div>

  if (health === undefined) {
    return (
      <>
        {windowSelect}
        {error ? <><p className="text-danger small mt-2" role="alert">{error}</p><ErrorReference error={failure} /></> : <p className="sw-hint mt-2" role="status">Loading…</p>}
      </>
    )
  }

  const status = STATUS[health.status] || { label: health.status || 'Unknown' }
  const channels = (metrics && metrics.byChannel) || []
  const outbox = (health.lanes || []).find(l => l.lane === 'outbox')
  const tiles = [
    { key: 'status', label: 'Status', value: status.label, tone: status.tone, detail: health.checkedAt ? `Checked ${formatAgo(health.checkedAt)}` : undefined },
    { key: 'raised', label: 'Raised', value: formatCount(metrics && metrics.raised), detail: WINDOWS.find(w => w.key === windowKey).label },
    { key: 'notifications', label: 'Notifications', value: formatCount(metrics && metrics.notifications), detail: 'one per recipient' },
    ...channels.map(c => ({ key: `failure-${c.channel}`, label: `${channelLabel(c.channel)} failures`, value: percent(c.failureRatePercent), tone: rateTone(Number(c.failureRatePercent) || 0), to: `${NOTIFY_BASE}/dead-letter` })),
    { key: 'suppressions', label: 'Suppressed (1 h)', value: formatCount(health.suppressionsLastHour) },
    ...(outbox ? [{ key: 'outbox', label: 'Outbox waiting', value: formatCount(outbox.depth), tone: outbox.breached ? 'bad' : undefined, detail: `oldest ${durationText(outbox.oldestDueAgeSeconds)}`, to: `${NOTIFY_BASE}/outbox` }] : [])
  ]

  const g = health.guardrails || {}
  const b = health.budgets || {}

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <span className="sw-hint small">{health.checkedAt ? `Health checked ${formatDateTime(health.checkedAt)}` : ''}</span>
        {windowSelect}
      </div>
      {error && <p className="text-danger small" role="alert">{error}</p>}
      <RollupTiles tiles={tiles} label="Notification health" />

      <div className="nf-grid-2 mt-3">
        <FleetSection id="nf-ov-channels" title="By channel" lede="Deliveries in the window, by what happened to them.">
          <table className="table nf-table" aria-label="By channel">
            <thead><tr><th>Channel</th><th>Statuses</th><th>Failure rate</th></tr></thead>
            <tbody>
              {channels.map(c =>
                <tr key={c.channel}>
                  <td>{channelLabel(c.channel)}</td>
                  <td className="small">{Object.entries(c.byStatus || {}).map(([k, v]) => `${k} ${formatCount(v)}`).join(' · ')}</td>
                  <td><Pill tone={rateTone(Number(c.failureRatePercent) || 0)}>{percent(c.failureRatePercent)}</Pill></td>
                </tr>)}
            </tbody>
          </table>
          {(metrics.byType || []).length > 0 &&
            <>
              <h3 className="h6 mt-2">Most raised</h3>
              <ul className="list-unstyled small m-0">
                {metrics.byType.slice(0, 8).map(t => <li key={t.type}><span className="sw-mono">{t.type}</span> <span className="sw-hint">{formatCount(t.count)}</span></li>)}
              </ul>
            </>}
        </FleetSection>

        <FleetSection id="nf-ov-series" title="Over time" lede="Raised, sent and failed per bucket.">
          <table className="table nf-table" aria-label="Over time">
            <thead><tr><th>From</th><th>Raised</th><th>Sent</th><th>Failed</th></tr></thead>
            <tbody>
              {(metrics.series || []).slice(-12).map(s =>
                <tr key={s.at}><td>{formatDateTime(s.at)}</td><td>{formatCount(s.raised)}</td><td>{formatCount(s.sent)}</td><td className={s.failed ? 'text-danger' : undefined}>{formatCount(s.failed)}</td></tr>)}
            </tbody>
          </table>
        </FleetSection>
      </div>

      <FleetSection id="nf-ov-lanes" title="Lanes" lede="How much is waiting in each lane, the oldest due item, and the p95 wait from due to claimed over the last 15 minutes, against its limit.">
        <div className="table-responsive">
          <table className="table nf-table" aria-label="Lanes">
            <thead><tr><th>Lane</th><th>Due now</th><th>Scheduled</th><th>Oldest due</th><th>Lag (p95)</th><th>Limit</th><th /></tr></thead>
            <tbody>
              {(health.lanes || []).map(l =>
                <tr key={l.lane}>
                  <td>{laneLabel(l.lane)}</td>
                  <td>{formatCount(l.depth)}</td>
                  <td>{formatCount(l.scheduled)}</td>
                  <td>{durationText(l.oldestDueAgeSeconds)}</td>
                  <td>{durationText(l.lagSeconds)}</td>
                  <td>{durationText(l.limitSeconds)}</td>
                  <td><LimitPill breached={l.breached} /></td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </FleetSection>

      <FleetSection id="nf-ov-providers" title="Providers" lede="Whether each provider sends for real or is captured, its circuit breaker, its error rate over 5 minutes and its latency.">
        <div className="table-responsive">
          <table className="table nf-table" aria-label="Providers">
            <thead><tr><th>Provider</th><th>Mode</th><th>Circuit</th><th>Errors (5 min)</th><th>Latency p50 / p95</th><th>Last success</th><th>Last error</th></tr></thead>
            <tbody>
              {(health.providers || []).map(p =>
                <tr key={p.provider}>
                  <td>{providerLabel(p.provider)}{p.credentialId ? <div className="sw-hint small">{`credential ${p.credentialId}`}</div> : null}</td>
                  <td><Pill tone={providerModeTone(p.mode)}>{providerModeLabel(p.mode)}</Pill></td>
                  <td>
                    <Pill tone={circuitTone(p.circuitState)}>{circuitLabel(p.circuitState)}</Pill>
                    {p.circuitState === 'open' && p.openUntil && <div className="sw-hint small">{`until ${formatDateTime(p.openUntil)}`}</div>}
                    {p.consecutiveFailures > 0 && <div className="sw-hint small">{`${p.consecutiveFailures} failures in a row`}</div>}
                  </td>
                  <td>{percent(p.errorRatePercent5m)}</td>
                  <td>{`${p.latencyP50Ms ?? '—'} / ${p.latencyP95Ms ?? '—'} ms`}</td>
                  <td>{p.lastSuccessAt ? formatAgo(p.lastSuccessAt) : '—'}</td>
                  <td className="sw-mono small">{p.lastError || ''}</td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </FleetSection>

      <div className="nf-grid-2">
        <FleetSection id="nf-ov-guardrails" title="Database guardrails" lede="Notify shares the database: a long transaction anywhere holds back cleanup of its queue tables. Staff alerts fire past these limits.">
          <table className="table nf-table" aria-label="Guardrails">
            <tbody>
              <tr><th scope="row">Oldest xmin</th><td>{durationText(g.oldestXminAgeSeconds)}</td><td>{durationText(g.xminLimitSeconds)}</td><td><LimitPill breached={Number(g.oldestXminAgeSeconds) > Number(g.xminLimitSeconds)} /></td></tr>
              <tr><th scope="row">Longest transaction</th><td>{durationText(g.longestTransactionSeconds)}</td><td>{durationText(g.longTxLimitSeconds)}</td><td><LimitPill breached={Number(g.longestTransactionSeconds) > Number(g.longTxLimitSeconds)} /></td></tr>
              {g.longestTransactionApp && <tr><th scope="row">Held by</th><td colSpan={3} className="sw-mono small">{g.longestTransactionApp}</td></tr>}
              {g.deliveriesDeadTuplePercent !== undefined && <tr><th scope="row">Dead rows in deliveries</th><td colSpan={3}>{percent(g.deliveriesDeadTuplePercent)}</td></tr>}
            </tbody>
          </table>
          {(g.brokerTriggers || []).length > 0 &&
            <>
              <h3 className="h6 mt-2">When to adopt a broker</h3>
              <table className="table nf-table" aria-label="Broker-adoption triggers">
                <tbody>
                  {g.brokerTriggers.map(t =>
                    <tr key={t.name}><th scope="row" className="fw-normal">{t.name}</th><td>{formatCount(t.value)}</td><td>{formatCount(t.limit)}</td><td><LimitPill breached={t.breached} /></td></tr>)}
                </tbody>
              </table>
            </>}
        </FleetSection>

        <FleetSection id="nf-ov-budgets" title="Budgets">
          <dl className="sw-dl">
            <dt>Push per person</dt><dd>{`${formatCount(b.loginPushHourly)} an hour, ${formatCount(b.loginPushDaily)} a day`}</dd>
            <dt>Per organization</dt><dd>{`${formatCount(b.orgDailyCap)} a day`}</dd>
            {b.providerRates && <><dt>Provider rates</dt><dd>{Object.entries(b.providerRates).map(([k, v]) => `${providerLabel(k)} ${formatCount(v)}/s`).join(' · ')}</dd></>}
            <dt>At their cap today</dt>
            <dd>{(b.orgsAtCap || []).length === 0 ? <span className="sw-hint">None</span> : b.orgsAtCap.map(o => <div key={o.organizationId}><span><OrgName id={o.organizationId} /></span>{` · ${formatCount(o.count)}`}</div>)}</dd>
          </dl>
          {(health.workers || []).length > 0 &&
            <>
              <h3 className="h6 mt-2">Workers</h3>
              <ul className="list-unstyled small m-0">
                {health.workers.map(w => <li key={w.name}><span className="sw-mono">{w.name}</span>{` · ${w.role}${(w.lanes || []).length ? ` (${w.lanes.join(', ')})` : ''} · ${w.version || ''} · seen ${formatAgo(w.seenAt)}`}</li>)}
              </ul>
            </>}
          {health.wake && <p className="sw-hint small mt-2 mb-0">{health.wake.enabled ? `Wake-ups on: ${formatCount(health.wake.received1h)} in the last hour.` : 'Wake-ups off: the relay polls only.'}</p>}
        </FleetSection>
      </div>
    </>
  )
}
